import { useContext, useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import { Col, OverlayTrigger, Popover, PopoverContent, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { LongArrowAltRight } from '@styled-icons/fa-solid';
import { ShowIf } from '../../../Security/security';
import { StoreContext } from '../../../Store';
import { getNewLoad, getNewLoadNeeDropdown, getEPO, getDestCountryOptions, updateEPOStatus } from '../../../apiCalls';
import { Input, Select, NeeNorSelect } from '../../../Input/Input';
import {
  generateAdjustmentsObject,
  incotermRequiresAdjustments,
  incotermAllowsAdjustments,
} from '../../../Adjustments/utils';
import { EpoJobDetailContext } from '../EpoJobDetails';
import { AdjustmentsModal } from '../../../Adjustments/AdjustmentsModal';
import { getAdjustmentsWarningMessage } from '../../../Util/ValidateLoadAdjustments';

export default function EpoJobHeaders({ editInvoiceData }) {
  const {
    jobDetails,
    setJobDetails,
    updateJobDetails,
    canSave,
    isCustomer,
    setShowUpdateEta,
    adjustmentsIncoLoaded,
    setAdjustmentsIncoLoaded,
    showAdjustmentsModal,
    setShowAdjustmentsModal,
    setIsFreightChargesInco,
    formRef,
    setServerData,
    loadIsReadOnly,
  } = useContext(EpoJobDetailContext);
  const store = useContext(StoreContext);
  const adjustmentsRef = useRef();
  const [euToGB, setEuToGB] = useState(null);
  const isImport = jobDetails?.isImport;
  const { id } = useParams();

  const [showAdjustmentsButton, setShowAdjustmentsButton] = useState(false);
  const [isAdjustmentsRequired, setIsAdjustmentsRequired] = useState(false);

  const showConsigneeField = isImport && !!jobDetails.representative;

  useEffect(() => {
    async function fetch() {
      const movementTypes = await getNewLoad('movement');
      const euToGBLocal = movementTypes.find((movement) => (movement.data = 'EU (mainland) - GB'));
      setEuToGB(euToGBLocal);
      getNewLoad('currency').then((data) => {
        store.currencyOptions[1](data);
      });
      getNewLoad('incoterm').then((data) => {
        store.incotermsOptions[1](data);
      });
      const countryOptions = await getDestCountryOptions(isImport, parseInt(id));
      store.dualCountryOptions[1](countryOptions);
    }
    if (typeof isImport === 'boolean') {
      fetch();
    }
  }, [adjustmentsIncoLoaded, isImport, id]);

  const updateAdjustments = (returnData) => {
    const existingAdjustments = returnData?.adjustments;
    if (Array.isArray(existingAdjustments) && existingAdjustments.length > 0) {
      adjustmentsRef.current = existingAdjustments.map((adj) => ({ ...adj }));
    } else {
      adjustmentsRef.current = generateAdjustmentsObject(returnData, store.currencyOptions[0]);
    }
  };

  useEffect(() => {
    setShowAdjustmentsButton(incotermAllowsAdjustments(jobDetails.incoterm));
    setIsAdjustmentsRequired(incotermRequiresAdjustments(jobDetails.incoterm?.toString()));
  }, [jobDetails.incoterm]);

  useEffect(() => {
    if (adjustmentsIncoLoaded && store.currencyOptions[0].length > 0) {
      updateAdjustments(jobDetails);
      updateAdjustmentsFromIncoTerm();
      setAdjustmentsIncoLoaded(false);
    }
  }, [jobDetails.incoterm, store.currencyOptions[0], adjustmentsIncoLoaded]);

  const updateIncoTerm = (data) => {
    updateJobDetails('incoterm', data);
    updateAdjustmentsFromIncoTerm(data);
  };

  const updateAdjustmentsFromIncoTerm = (data) => {
    const incoterm = data?.toString() ?? jobDetails.incoterm?.toString();
    let currentAdjustments = adjustmentsRef.current ?? jobDetails.adjustments;

    // If we have no adjustments or an empty list, build defaults so the modal always has rows
    if (!currentAdjustments || currentAdjustments.length === 0) {
      currentAdjustments = generateAdjustmentsObject(jobDetails, store.currencyOptions[0]);
    }

    const nextAdjustments = currentAdjustments.map((adjustment) => ({
      ...adjustment,
      required: adjustment.name === 'freightCharges' ? incotermRequiresAdjustments(incoterm) : adjustment.required,
    }));

    updateJobDetails('adjustments', nextAdjustments);
    adjustmentsRef.current = null;

    if (jobDetails.isImport) {
      setIsFreightChargesInco(incotermRequiresAdjustments(incoterm));
    }
  };

  const displayPort = () => {
    const { exitPort, entryPort } = jobDetails;
    return (
      <Col>
        <Input disabled label={isImport ? 'Entry Port' : 'GB Port of Exit'} value={isImport ? entryPort : exitPort} />
      </Col>
    );
  };

  const displayNeeNorCode = () => {
    const consignorLabel = isImport ? 'Consignor Code' : 'Consignee Code';
    const name = isImport ? 'Consignor' : 'Consignee';
    const isDisabled = !canSave || isCustomer || !editInvoiceData || loadIsReadOnly;
    return (
      <NeeNorSelect
        label={consignorLabel}
        name={name}
        disabled={isDisabled}
        data={jobDetails.consignor}
        setData={(data) => updateJobDetails('consignor', data)}
        refresh={async (search, skip) => {
          if (typeof jobDetails.newLoadId === 'number') {
            return await getNewLoadNeeDropdown('neenor', jobDetails.newLoadId, name, search, skip);
          }
        }}
        dependencies={[euToGB]}
        dropdownWidth="720px"
      />
    );
  };

  const updateStatus = async () => {
    await updateEPOStatus(jobDetails.id);

    const data = await getEPO(id, 'Header');
    setJobDetails({ id, ...data.Parents[0] });
    setServerData({ id, ...data.Parents[0] });
    // Trigger adjustments regeneration after status change so the modal is populated
    setAdjustmentsIncoLoaded(true);
  };

  let loadAdjustmentsWarningMessage = '';
  jobDetails?.adjustments?.forEach((adjustment) => {
    const { warningMessage: adjustmentWarning } = getAdjustmentsWarningMessage(adjustment);
    if (adjustmentWarning) {
      loadAdjustmentsWarningMessage += adjustmentWarning;
    }
  });

  return (
    <div>
      <div className="registrationText mb-2">Company Information</div>
      <Row>
        {!showConsigneeField && (
          <>
            <Col>
              <Input disabled label="Company Name" value={isImport ? jobDetails.compName : jobDetails.consignorName} />
            </Col>
            <Col>
              <Input disabled label="EORI Number" value={jobDetails.EORI} />
            </Col>
          </>
        )}
        {showConsigneeField && (
          <Col>
            <NeeNorSelect
              label="Consignee Code"
              name="Consignee"
              disabled={!canSave || isCustomer || !editInvoiceData || loadIsReadOnly}
              data={jobDetails.repConsignee || jobDetails.consignee}
              setData={(data) => updateJobDetails('repConsignee', data)}
              refresh={async (search, skip) => {
                if (typeof jobDetails.newLoadId === 'number') {
                  return await getNewLoadNeeDropdown('neenor', jobDetails.newLoadId, 'Consignee', search, skip);
                }
              }}
              dependencies={[euToGB]}
              dropdownWidth="720px"
            />
          </Col>
        )}
        <Col>
          <Input disabled label="Originator" value={jobDetails.originator} />
        </Col>
        <Col>
          <Input disabled label="Submitter" value={jobDetails.submittedBy} />
        </Col>
      </Row>

      <div className="registrationText my-2">Submitted Data</div>
      <Row>
        {displayPort()}
        <Col>
          <ShowIf isCustomer>
            <div
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                setShowUpdateEta(true);
              }}
            >
              <Input
                type="date"
                label={jobDetails.isImport ? 'Date of Arrival' : 'Date of Exit'}
                value={jobDetails.newArrivalDate ?? jobDetails.estimatedDateOfLoading}
                info={<span className="green-text text-decoration-underline bold">Amend</span>}
              />
            </div>
          </ShowIf>
          <ShowIf isCustomer={false}>
            <Input
              type="date"
              label={jobDetails.isImport ? 'Date of Arrival' : 'Date of Exit'}
              value={jobDetails.newArrivalDate ?? jobDetails.estimatedDateOfLoading}
              disabled
              info={
                !!jobDetails.newArrivalDate && (
                  <OverlayTrigger
                    placement="right"
                    trigger={['hover', 'focus']}
                    overlay={
                      <Popover style={{ minWidth: '20rem' }}>
                        <PopoverContent>
                          <h6>Arrival Date Amended</h6>
                          <p>The trader has changed their arrival date since first creating this consignment.</p>
                          <div
                            className="d-flex justify-content-between align-items-center"
                            style={{ fontSize: '0.8rem' }}
                          >
                            <div className="text-center">
                              <header className="fw-bolder text-eoriblue">Original Arrival Date</header>
                              <div className="fw-bolder red-text">{jobDetails?.estimatedDateOfLoading}</div>
                            </div>
                            <LongArrowAltRight width="1.5em" height="1.5em" />
                            <div className="text-center">
                              <header className="fw-bolder text-eoriblue">New Arrival Date</header>
                              <div className="fw-bolder green-text">{jobDetails.newArrivalDate}</div>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    }
                  >
                    <div className="warning"></div>
                  </OverlayTrigger>
                )
              }
            />
          </ShowIf>
        </Col>
        <Col>
          <Input label="Job Reference" value={jobDetails.jobReference} className="formattedText" disabled />
        </Col>
      </Row>
      <div className="registrationText my-2">Invoice Data</div>
      <Row>
        <Col>
          <Select
            label={jobDetails.isImport ? 'Country of Despatch' : 'Destination Country'}
            name="countrydespatch"
            disabled={!canSave || isCustomer || !editInvoiceData || loadIsReadOnly}
            data={jobDetails.despatchCountry}
            setData={(data) => {
              updateJobDetails('despatchCountry', data);
            }}
            required
            options={store.dualCountryOptions[0]}
          />
        </Col>
        <Col>{displayNeeNorCode()}</Col>
      </Row>
      <Row>
        <Col>
          <Select
            label="Invoice Currency"
            disabled={!canSave || isCustomer || !editInvoiceData || loadIsReadOnly}
            data={jobDetails.currency}
            setData={(data) => updateJobDetails('currency', data)}
            required
            options={store.currencyOptions[0]}
          />
        </Col>
        <Col>
          <Select
            label="Incoterm"
            disabled={!canSave || isCustomer || !editInvoiceData || loadIsReadOnly}
            data={jobDetails.incoterm}
            setData={(data) => updateIncoTerm(data)}
            options={store.incotermsOptions[0]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <ShowIf
            or={[
              { department: 'OPS', role: 'SUPERUSER' },
              { department: 'QA', role: 'SUPERUSER' },
              { department: 'OPS', role: 'STANDARD' },
              { department: 'QA', role: 'STANDARD' },
            ]}
          >
            {jobDetails.EPOStatus !== 'Completed' && (
              <div className="registrationText my-2">
                Change Job status to:
                <button
                  type="button"
                  className="blue-button-teams"
                  disabled={loadIsReadOnly}
                  style={{
                    maxWidth: 'max-content',
                    borderRadius: '10px',
                    width: 'auto',
                    whiteSpace: 'nowrap',
                    paddingLeft: '15px',
                    paddingRight: '15px',
                    marginLeft: '10px',
                  }}
                  onClick={updateStatus}
                >
                  {jobDetails.PrevStatus}
                </button>
              </div>
            )}
          </ShowIf>
        </Col>
        <Col>
          {showAdjustmentsButton && (
            <>
              <div className={`input-outer-container`}>
                <div className="input-label my-1">
                  <button
                    type="button"
                    className="invoice-add-btn green-text"
                    onClick={(e) => {
                      setShowAdjustmentsModal(true);
                    }}
                  >
                    <span className="text-decoration-underline" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                      Adjustments
                    </span>
                    <span>
                      <OverlayTrigger
                        placement="top"
                        trigger={['hover', 'focus']}
                        overlay={
                          <Popover style={{ minWidth: '15rem' }}>
                            <PopoverContent>
                              <h6>Adjustments</h6>
                              <p>
                                {isAdjustmentsRequired
                                  ? `Freight Charges is mandatory for this Incoterm.`
                                  : `Adjustments are optional for this Incoterm.`}
                              </p>
                            </PopoverContent>
                          </Popover>
                        }
                      >
                        <div className="info" style={{ marginBottom: '-5px' }}></div>
                      </OverlayTrigger>
                    </span>
                  </button>
                </div>
              </div>
              {loadAdjustmentsWarningMessage && (
                <span className="input-warning-box">{loadAdjustmentsWarningMessage}</span>
              )}
            </>
          )}
        </Col>
      </Row>
      <AdjustmentsModal
        adjustments={jobDetails.adjustments}
        setAdjustments={(newValues) => {
          updateJobDetails(
            'adjustments',
            _.cloneDeep(
              newValues.map((adj) => {
                adj.error = undefined;
                return adj;
              })
            )
          );
        }}
        currencyOptions={store.currencyOptions[0]}
        show={showAdjustmentsModal}
        onHide={() => {
          setShowAdjustmentsModal(false);
        }}
        readOnly={!editInvoiceData || !canSave || isCustomer}
        ref={formRef}
      />
    </div>
  );
}
